import { Component, type ErrorInfo, type ReactNode } from 'react';
import { env } from './env.ts';

// Monitoreo de errores con Sentry. Sin VITE_SENTRY_DSN queda desactivado (solo consola) y
// el SDK ni siquiera se descarga. Nunca se envían datos personales: solo ids técnicos
// (negocio, dispositivo) y el rol.

type SentryModule = typeof import('@sentry/react');
let sentry: SentryModule | null = null;
let context: { businessId?: string; deviceId?: string; role?: string } = {};

export async function initMonitoring(): Promise<void> {
  if (!env.sentryDsn) return;
  try {
    sentry = await import('@sentry/react');
    sentry.init({
      dsn: env.sentryDsn,
      release: `pos-app@${env.appVersion}`,
      environment: import.meta.env.MODE,
      beforeSend(event) {
        if (event.user) event.user = { id: event.user.id };
        return event;
      },
    });
    applyContext();
  } catch (error) {
    console.warn('No se pudo iniciar el monitoreo de errores', error);
  }
}

function applyContext() {
  if (!sentry) return;
  sentry.setTag('business_id', context.businessId ?? 'none');
  sentry.setTag('device_id', context.deviceId ?? 'none');
  sentry.setTag('role', context.role ?? 'none');
}

export function setMonitoringContext(next: { businessId?: string; deviceId?: string; role?: string }): void {
  context = next;
  applyContext();
}

export function reportError(error: unknown, extra?: Record<string, unknown>): void {
  console.error(error, extra);
  sentry?.captureException(error, { extra });
}

/** Captura errores de renderizado, los reporta y muestra una pantalla amigable. */
export class ErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack });
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// Errores fuera de React (promesas sin catch, etc.).
window.addEventListener('unhandledrejection', (event) => reportError(event.reason));
